import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, Observable, of, startWith, Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

import { MaterialModule } from 'src/app/material.module';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { PharmacyService } from '../pharmacy.service';
import {
  DrugInventoryLookup,
  BulkStockInCommand,
  StockInDialogData,
} from 'src/app/models/pharmacy.model';
import { DialogUtils } from 'src/app/common/utils/dialog.utils';

@Component({
  selector: 'app-stock-in-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule,
  ],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './stock-in-dialog.component.html',
  styleUrl: './stock-in-dialog.component.scss',
})
export class StockInDialogComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  stockInForm: FormGroup;
  inventoryLookup: DrugInventoryLookup[] = [];
  minDate: Date = new Date();

  constructor(
    private fb: FormBuilder,
    private pharmacyService: PharmacyService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<StockInDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StockInDialogData,
    private dialogUtils: DialogUtils,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadInventoryLookup();
  }

  private initForm(): void {
    this.stockInForm = this.fb.group({
      reference: [''],
      items: this.fb.array([this.createRow()]),
    });
  }

  get items(): FormArray {
    return this.stockInForm.get('items') as FormArray;
  }

  createRow(): FormGroup {
    return this.fb.group({
      drugInventoryId: [null, Validators.required],
      drugCtrl: [''],
      drugName: [''],
      batchNo: ['', Validators.required],
      expiryDate: [null, Validators.required],
      quantity: [null, [Validators.required, Validators.min(1)]],
      purchasePrice: [null],
      sellingPrice: [null],
    });
  }

  onAddRow(): void {
    this.items.push(this.createRow());
  }

  onRemoveRow(index: number): void {
    if (this.items.length <= 1) {
      this.items.at(0).reset();
    } else {
      this.items.removeAt(index);
    }
  }

  getFilteredDrugsForRow(index: number): Observable<DrugInventoryLookup[]> {
    const row = this.items.at(index);
    const ctrl = row.get('drugCtrl');
    if (!ctrl) return of([]);

    return ctrl.valueChanges.pipe(
      startWith(ctrl.value || ''),
      map(value => {
        const filterVal = typeof value === 'string' ? value.toLowerCase() : '';
        return this.inventoryLookup.filter(d =>
          d.drugName.toLowerCase().includes(filterVal)
        );
      })
    );
  }

  displayDrugLookup(drug: DrugInventoryLookup): string {
    return drug ? drug.drugName : '';
  }

  onDrugSelected(drug: DrugInventoryLookup, row: AbstractControl): void {
    (row as FormGroup).patchValue({
      drugInventoryId: drug.id,
      drugName: drug.drugName,
      sellingPrice: drug.sellingPrice,
    });
  }

  onSave(): void {
    if (this.stockInForm.invalid) {
      this.toastr.warning(this.translate.instant('PHARMACY.FILL_ALL_FIELDS'));
      return;
    }

    const command: BulkStockInCommand = {
      reference: this.stockInForm.get('reference')?.value || undefined,
      items: this.items.controls.map(row => ({
        drugInventoryId: row.get('drugInventoryId')?.value,
        batchNo: row.get('batchNo')?.value,
        expiryDate: row.get('expiryDate')?.value,
        quantity: Number(row.get('quantity')?.value),
        purchasePrice: row.get('purchasePrice')?.value
          ? Number(row.get('purchasePrice')?.value)
          : undefined,
        sellingPrice: row.get('sellingPrice')?.value
          ? Number(row.get('sellingPrice')?.value)
          : undefined,
      })),
    };

    this.pharmacyService.bulkStockIn(command)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response.isSuccess) {
          this.toastr.success(this.translate.instant('PHARMACY.STOCK_IN_SUCCESS'));
          this.dialogUtils.closeSidePanel(this.dialogRef, response.result);
        } else {
          const allErrors = Object.values(response.errors).flat();
          this.toastr.error(allErrors.join('\n'));
        }
      });
  }

  onClose(): void {
    this.dialogUtils.closeSidePanel(this.dialogRef);
  }

  private loadInventoryLookup(): void {
    this.pharmacyService.getInventoryLookup()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response.isSuccess) {
          this.inventoryLookup = response.result;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isChargeSelected(row: AbstractControl): boolean {
    const drugId = row.get('drugInventoryId')?.value;
    return !!drugId;
  }
}
